import { inject, Injectable, signal } from '@angular/core';
import { IProduktAuswahlDetails, IProduktSelektion, PreisIndikation, TariffOptionSap, TariffSelectionCardData } from '@mwe/models';
import { AriaLiveService, LoggingService, TariffSelectionLogic } from '@mwe/services';
import { getTariffOptionsIdList, isArrayWithMinOneItem, isProductCategoryEmobility } from '@mwe/utils';
import { tariffSelectionStoreUtils } from './tariff-selection-store.utils';
import { ServiceStateEnum } from '@mwe/constants';
import { VoucherFormComponent } from '../../voucher/voucher-form/voucher-form.component';
import { concatMap, from, mergeAll, of } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { TariffSelectionStore } from './tariff-selection.store';
import { TariffSelectionStoreInitParams } from './tariff-selection-store.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class TariffSelectionCardLogic {
  // helper method, after every tariff-option-change we have to validate the voucher...
  // TODO: refactor VoucherFormComponent so we can reuse logic/service for voucher-validation instead of VoucherFormComponent
  // TODO: This Service needs to be refactored after all VoucherFormComponent are removed.
  doSomeVoucherFormMagic: (selection: IProduktSelektion) => VoucherFormComponent;

  private readonly store = inject(TariffSelectionStore);
  private readonly selectionLogic = inject(TariffSelectionLogic);
  private readonly loggingService = inject(LoggingService);
  private readonly ariaLiveService = inject(AriaLiveService);
  private readonly translateService = inject(TranslateService);

  private readonly updateAria = signal<boolean>(false);

  reset(): void {
    this.doSomeVoucherFormMagic = null;
    this.store.reset();
  }

  initializeStoreData(data: TariffSelectionStoreInitParams): void {
    if (!isArrayWithMinOneItem(data.allProducts)) {
      return;
    }

    this.store.initializeStore(data);

    let newSelectedTariffs = [] as TariffSelectionCardData[];
    // user selection in session storage
    if (isArrayWithMinOneItem(data.userSelectedItems)) {
      newSelectedTariffs = [
        ...newSelectedTariffs,
        ...this.getUtils().restoreSelectedTariffsFromSessionStorage(
          data.allProducts,
          data.allPriceIndications,
          data.userSelectedItems,
          data.userSelectedPriceIndications,
        ),
      ];
    }
    if (data.defaultProducts) {
      newSelectedTariffs = [
        ...newSelectedTariffs,
        ...this.getUtils().getMissingItemsAsDefault(
          data.allProducts,
          data.allPriceIndications,
          data.userSelectedItems,
          data.defaultProducts,
        ),
      ];
    }
    const sortedTariffs = this.getUtils().sortTariffs(newSelectedTariffs);
    this.store.initializeSelectedTariffs(sortedTariffs);
  }

  async checkInitialVoucherCode(): Promise<void> {
    const selection: IProduktSelektion = this.getProductSelectionFromSelectedTariffs();
    await this.getVoucherCode(selection, true);
    await this.handleVoucherCodeUpdate();
  }

  async handleVoucherCodeUpdate(predefinedCode?: string): Promise<void> {
    this.updateAllStates(ServiceStateEnum.LOADING);

    const selectedTariffs = predefinedCode !== undefined ? this.store.selectedTariffsForNewVoucher() : this.store.selectedTariffs();

    //TODO: before MWE-16647 this wasnt needed, to hold codefreeze date i added this hack but i feel like theres a cleaner solution
    // TODO: optimize this
    for (const tariff of selectedTariffs) {
      await this.updateSinglePriceSheetSearch(this.store.zipCode(), tariff);
    }

    from(selectedTariffs)
      .pipe(
        filter(tariff => {
          const hasMatch = this.hasMatchingPriceSheet(tariff);

          if (!hasMatch) {
            this.updateCardData({
              ...tariff,
              state: ServiceStateEnum.ERROR,
            });
          }

          return hasMatch;
        }),
        map(tariff =>
          from(this.getNewPriceIndicationForUpdatedTariffOptionAfterVoucherUpdate(tariff, tariff.userSelection, predefinedCode)),
        ),
        mergeAll(),
        concatMap((data: { indication: PreisIndikation; tariff: TariffSelectionCardData; state: ServiceStateEnum }) => {
          this.updateCardData({
            ...data.tariff,
            priceIndication: data.indication,
            state: data.state,
          });
          return of();
        }),
      )
      .subscribe();
  }

  private hasMatchingPriceSheet(tariff: TariffSelectionCardData): boolean {
    if (isProductCategoryEmobility(tariff?.category)) {
      return true;
    }
    return this.selectionLogic.hasMatchingPriceSheet(tariff?.priceIndication?.tarif?.ISUTarifKey);
  }

  async handleTariffOptionChange(tariffData: TariffSelectionCardData, newSelectedOption: TariffOptionSap): Promise<void> {
    this.store.updateCardLoadingState(tariffData, ServiceStateEnum.LOADING);

    const everySelectionItemNotUpdated = tariffData.userSelection.filter(u => u.art !== newSelectedOption.art);
    const userSelection = [...everySelectionItemNotUpdated, newSelectedOption];

    this.updateAria.set(true);

    try {
      const priceIndication = await this.getNewPriceIndicationForUpdatedTariffOption(tariffData, userSelection, true);

      await this.updateSinglePriceSheetSearch(this.store.zipCode(), tariffData);

      if (!this.hasMatchingPriceSheet(tariffData)) {
        this.updateCardData({
          ...tariffData,
          state: ServiceStateEnum.ERROR,
        });
      } else {
        this.updateCardData({
          ...tariffData,
          userSelection,
          priceIndication,
          state: ServiceStateEnum.SUCCESS,
        });
      }
    } catch (error) {
      this.loggingService.logError(error, 'TariffSelectionCardLogic/handleTariffOptionChange');
      this.updateCardData({
        ...tariffData,
        userSelection,
        state: ServiceStateEnum.ERROR,
      });
    }
  }

  updateCardData(newTariff: TariffSelectionCardData): void {
    this.store.updateCardData(newTariff);
    this.updateAriaLiveText(newTariff);
  }

  async updateSinglePriceSheetSearch(postCode: string, tariffData: TariffSelectionCardData): Promise<void> {
    if (isProductCategoryEmobility(tariffData?.category)) {
      return;
    }

    const productSelectionFromSelectedTariffs: IProduktAuswahlDetails =
      this.getProductSelectionFromSelectedTariffs()[tariffData?.category?.toLowerCase()] ??
      this.getProductSelectionFromSelectedTariffs()?.anlagen?.find(
        anlage => anlage?.tarif?.ISUTarifKey?.toLowerCase() === tariffData?.priceIndication?.tarif?.ISUTarifKey?.toLowerCase(),
      );

    await this.selectionLogic.setPriceSheetSearchesFromProducts(postCode, [productSelectionFromSelectedTariffs]);
  }

  updateAriaLiveText(newTariff: TariffSelectionCardData): void {
    let ariaLiveText: string;
    if (this.updateAria()) {
      ariaLiveText = this.translateService.instant('tariff.ariaLive.newPrice', {
        arbeitspreis: newTariff.priceIndication.arbeitspreis,
        arbeitspreisUnit: newTariff.priceIndication.arbeitspreisUnit,
        grundpreis: newTariff.priceIndication.grundpreis,
        grundpreisUnit: newTariff.priceIndication.grundpreisUnit,
      });
      if (newTariff.priceIndication.aktionsHinweis) {
        ariaLiveText += this.translateService.instant('tariff.ariaLive.discountPresent', {
          aktionsHinweis: newTariff.priceIndication.aktionsHinweis,
        });
      }
      if (newTariff.priceIndication.istRabattiert) {
        ariaLiveText += this.translateService.instant('tariff.ariaLive.discountActive');
      }
      this.ariaLiveService.currentAriaLiveText.set(ariaLiveText);
    }
  }

  async getNewPriceIndicationForUpdatedTariffOption(
    selectedTariff: TariffSelectionCardData,
    userSelection: TariffOptionSap[],
    withVoucherCodeValidation: boolean,
    predefinedCode?: string,
  ): Promise<PreisIndikation> {
    // tariff-change can have tariffs without options, so you don't need an update-request for other prices
    if (!isArrayWithMinOneItem(selectedTariff.priceIndication?.tarif?.tarifOptionen)) {
      const copyPriceIndication = { ...selectedTariff.priceIndication } as PreisIndikation;
      if (selectedTariff.anlageId) {
        copyPriceIndication.anlageId = selectedTariff.anlageId;
      }
      return copyPriceIndication;
    }

    let voucherCode: string = predefinedCode;

    if (!predefinedCode) {
      const updatedTariff = {
        ...selectedTariff,
        userSelection,
      };

      // create temp selection for voucher code validation
      // first step: create selection with values before update
      const selection: IProduktSelektion = this.getProductSelectionFromSelectedTariffs();
      const updatedProduct = this.getUtils().createNewProduct(this.store.allProducts(), updatedTariff);
      // second step: update specific selection with new tariff-options
      if (selectedTariff.anlageId) {
        // tariff change
        const unaffectedAnlagen = selection.anlagen.filter(a => a.anlageId !== selectedTariff.anlageId);
        selection.anlagen = [...unaffectedAnlagen, updatedProduct];
      } else {
        // new client
        selection[selectedTariff.category.toLowerCase()] = updatedProduct;
      }

      voucherCode = await this.getVoucherCode(selection, withVoucherCodeValidation);
    }

    const response = await this.selectionLogic.getUpdatedPriceIndications(
      this.store.zipCode(),
      selectedTariff.category,
      selectedTariff.tariffKey,
      getTariffOptionsIdList(userSelection),
      voucherCode,
    );

    // tariff-change
    if (selectedTariff.anlageId) {
      response.indication.anlageId = selectedTariff.anlageId;
    }

    return response.indication;
  }

  async getVoucherCode(selection: IProduktSelektion, withVoucherCodeValidation: boolean): Promise<string> {
    const voucherFormRef = this.doSomeVoucherFormMagic?.(selection);
    if (voucherFormRef?.code && withVoucherCodeValidation) {
      await voucherFormRef.checkCode();
    }

    // wl voucher initialized todo refactor VoucherFormComponent to have a better way
    if (voucherFormRef?.showWLFields && !voucherFormRef.hasValidWLFormData()) {
      return undefined;
    }

    return voucherFormRef?.hasInvalidCode() ? undefined : voucherFormRef?.code;
  }

  getProductSelectionFromSelectedTariffs(): IProduktSelektion {
    return this.store.getProductSelection();
  }

  updateAllStates(state: ServiceStateEnum): void {
    this.store.updateAllCardLoadingStates(state);
  }

  updateAdditionalOptionGroupName(additionalOptionGroupName: string): void {
    this.store.updateAdditionalOptionGroupName(additionalOptionGroupName);
  }

  // helper function for rxjs-construct
  private async getNewPriceIndicationForUpdatedTariffOptionAfterVoucherUpdate(
    tariff: TariffSelectionCardData,
    userSelection: TariffOptionSap[],
    predefinedCode?: string,
  ): Promise<{ indication: PreisIndikation; tariff: TariffSelectionCardData; state: ServiceStateEnum }> {
    try {
      const indication = await this.getNewPriceIndicationForUpdatedTariffOption(tariff, userSelection, false, predefinedCode);
      return { indication, tariff, state: ServiceStateEnum.SUCCESS };
    } catch (error) {
      this.loggingService.logError(error, 'TariffSelectionCardLogic/handleVoucherCodeUpdate');
      return { indication: tariff.priceIndication, tariff, state: ServiceStateEnum.ERROR };
    }
  }

  // used for unit tests, to mock utils
  private getUtils() {
    return tariffSelectionStoreUtils;
  }
}
